"use client";

import { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Languages } from "lucide-react";

import { updateLocaleCookie } from "@/i18n/actions";
import { cn } from "@/utils";

const languages = [
  { code: "en", flag: "🇬🇧" },
  { code: "fr", flag: "🇫🇷" },
  { code: "de", flag: "🇩🇪" },
  { code: "es", flag: "🇪🇸" },
  { code: "it", flag: "🇮🇹" },
] as const;

type LanguageCode = (typeof languages)[number]["code"];

interface AuthLanguageSelectorProps {
  className?: string;
  position?: "top" | "bottom";
}

export function AuthLanguageSelector({ className, position = "top" }: AuthLanguageSelectorProps) {
  const t = useTranslations("auth.language");
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLocale, setCurrentLocale] = useState<LanguageCode>("en");

  useEffect(() => {
    const locale = document.cookie
      .split("; ")
      .find((row) => row.startsWith("locale="))
      ?.split("=")[1] as LanguageCode | undefined;
    if (locale && languages.some((l) => l.code === locale)) {
      setCurrentLocale(locale);
    }
  }, []);

  const handleLanguageChange = async (locale: LanguageCode) => {
    setIsOpen(false);
    startTransition(async () => {
      await updateLocaleCookie(locale);
      setCurrentLocale(locale);
      router.refresh();
    });
  };

  const currentLanguage = languages.find((l) => l.code === currentLocale);

  return (
    <div className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isPending}
        className={cn(
          "border-border bg-background text-foreground hover:bg-accent flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition-all duration-200",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
        aria-label={t("label")}
      >
        <Languages className="h-4 w-4" />
        <span>{currentLanguage?.flag}</span>
        <span className="hidden sm:inline">{t(currentLocale)}</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            className={cn(
              "border-border bg-background absolute right-0 z-50 min-w-[160px] rounded-xl border p-1 shadow-lg",
              position === "top" ? "bottom-full mb-2" : "top-full mt-2",
            )}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => handleLanguageChange(lang.code)}
                className={cn(
                  "hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                  currentLocale === lang.code && "bg-accent",
                )}
              >
                <span>{lang.flag}</span>
                <span>{t(lang.code)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
